import { DateTime } from "luxon"
import { EventDropArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { EventsForServiceCenterResponse } from "../../../createOrderApi"
import { DeliveryWithCart } from "../../form/formTypes"
import { isAllDayAvailabilitySlot } from "../calendar/helpers"

import { TimelineCalendarEvent, TimelineCalendarEventTimeRange } from "../calendar/fullCalendarTypes"

export const LOCAL_EVENT = "local-"
const LOCAL_EVENT_COLOR = tailwindPalette.blue[300]
export const DEFAULT_DURATION_MINUTES = 30
export const isLocalEvent = (id: string) => id?.startsWith(LOCAL_EVENT)
export const isCanEditExistOrder = (info: EventDropArg | EventResizeDoneArg) => !!info.event?.extendedProps["canEdit"]

export const createNewEvent = (
    resourceId: string,
    start: string,
    end: string,
    timeRange?: TimelineCalendarEventTimeRange,
): TimelineCalendarEvent => ({
    id: `${LOCAL_EVENT}${resourceId}-${start}`,
    resourceIds: [resourceId],
    start,
    end,
    backgroundColor: LOCAL_EVENT_COLOR,
    timeRange,
})

export const createInitEvents = (resourceIds: string[], start: string, end: string): TimelineCalendarEvent => {
    const firstResourceId = resourceIds[0] || "book-period"

    return {
        id: `${LOCAL_EVENT}${firstResourceId}-${start}`,
        resourceIds,
        start,
        end,
        backgroundColor: LOCAL_EVENT_COLOR,
    }
}

export function roundDateTimeToNearestMinutes(dateTime: DateTime, nearestMinutes: number): DateTime {
    const rounded = Math.ceil(dateTime.minute / nearestMinutes) * nearestMinutes
    return dateTime.set({ minute: rounded, second: 0, millisecond: 0 })
}

export function roundUpNowForBooking(now: DateTime, intervalMinutes: number): DateTime {
    const roundedNowBase = now.second !== 0 || now.millisecond !== 0 ? now.startOf("minute").plus({ minutes: 1 }) : now
    const remainder = roundedNowBase.minute % intervalMinutes
    if (remainder === 0) return roundedNowBase.set({ second: 0, millisecond: 0 })
    return roundedNowBase.plus({ minutes: intervalMinutes - remainder }).set({ second: 0, millisecond: 0 })
}

export function getBookingRangeFromAvailabilitySlot(
    args: Readonly<{
        dateTimeFactory: DateTimeFactory
        slotStart: DateTime
        slotEnd: DateTime
        now: DateTime
        intervalMinutes: number
        allDayStartHour: number
        maxDurationHours: number
    }>,
): { effectiveStart: DateTime; adjustedEnd: DateTime; originalEnd: DateTime } | null {
    const { dateTimeFactory, slotStart, slotEnd, now, intervalMinutes, allDayStartHour, maxDurationHours } = args

    if (!slotStart.isValid || !slotEnd.isValid) return null
    if (!(slotEnd > slotStart)) return null

    // Slot is entirely in the past -> do not allow booking.
    if (slotEnd <= now) return null

    const roundedNow = roundUpNowForBooking(now, intervalMinutes)

    const startBase = isAllDayAvailabilitySlot(dateTimeFactory, slotStart, slotEnd)
        ? slotStart.set({ hour: allDayStartHour, minute: 0, second: 0, millisecond: 0 })
        : slotStart

    const effectiveStart = startBase < now ? roundedNow : startBase
    if (effectiveStart >= slotEnd) return null

    const desiredEnd = effectiveStart.plus({ hours: maxDurationHours })
    const adjustedEnd = desiredEnd < slotEnd ? desiredEnd : slotEnd

    return { effectiveStart, adjustedEnd, originalEnd: slotEnd }
}

export function earliestTime(times: DeliveryWithCart[]): string {
    return times.map((t) => t.time!).sort((a, b) => a.localeCompare(b))[0]
}

export const splitIntoThreeDays = (dateTimeFactory: DateTimeFactory, startStr: string, endStr: string) => {
    const start = dateTimeFactory.fromISO(startStr)
    const hardEnd = dateTimeFactory.fromISO(endStr)
    // Build [day0, day1, day2], each clamped to fetchInfo.endStr (exclusive)
    return [0, 1, 2].map((i) => {
        const s = start.plus({ days: i })
        const e = s.plus({ days: 1 })
        const endClamped = DateTime.min(e, hardEnd)
        return {
            startISO: dateTimeFactory.toApiISOMinutesFormat(s),
            endISO: dateTimeFactory.toApiISOMinutesFormat(endClamped),
        }
    })
}

export const mergeUniqueEvents = (parts: EventsForServiceCenterResponse[]) => {
    const map = new Map<number, NonNullable<EventsForServiceCenterResponse["events"]>[number]>()
    for (const res of parts) {
        for (const ev of res?.events ?? []) {
            if (!map.has(ev.orderId)) map.set(ev.orderId, ev)
        }
    }
    return Array.from(map.values())
}
